import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { complianceApi } from "@bw/shared/api";
import type {
  UserDataExport,
  PendingUserDeletionResponse,
} from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The caller's open right-to-delete tombstone (GET /me/deletion-status). Returns
 * a *pending* tombstone, or null when none is open. SERVER-SCOPED to the auth
 * user. The deletion mutation invalidates this arm so the page re-reads the
 * server's truth — never an optimistic local flip. A pending row represents
 * interrupted deletion work awaiting recovery; deletion cannot be cancelled.
 */
export function useDeletionStatus() {
  return useQuery<PendingUserDeletionResponse | null>({
    queryKey: queryKeys.me.deletionStatus(),
    queryFn: () => complianceApi.getDeletionStatus(),
  });
}

/**
 * Download my data (GET /me/data-export). A mutation (not a query): it fires only
 * on the user's explicit click. The data is the SERVER's — the caller offers it
 * verbatim as a JSON download.
 */
export function useDataExport() {
  return useMutation<UserDataExport, Error, void>({
    mutationFn: () => complianceApi.getMyDataExport(),
  });
}

/**
 * Request account deletion (DELETE /me). IMMEDIATE and PERMANENT (grace = 0). The
 * confirmation email is forwarded to the client (anti-session-hijack — the
 * backend rejects a mismatch). The status arm is invalidated after either result:
 * success normally refetches null after execution, while a cleanup failure may
 * have already committed a non-cancellable pending recovery tombstone. No
 * optimistic UI.
 */
export function useRequestDeletion() {
  const qc = useQueryClient();
  return useMutation<PendingUserDeletionResponse, Error, string>({
    mutationFn: (confirmationEmail: string) =>
      complianceApi.requestAccountDeletion(confirmationEmail),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.me.deletionStatus() });
    },
  });
}
